export interface BlogUrlConfig {
  useCustomDomain: boolean;
  customDomain: string | null;
  usernameSlug: string | null;
  /** Domein van de gekoppelde joepa-site (alleen gezet als de koppeling actief
   * is). Blogs staan daar als echte pagina's op /blog/<slug>, dus deze wint
   * van alle andere varianten. */
  joepaSiteUrl?: string | null;
}

/** joepa-site-URL genormaliseerd naar 'https://domein' zonder trailing slash. */
function joepaBase(config: BlogUrlConfig): string | null {
  if (!config.joepaSiteUrl) return null;
  const url = config.joepaSiteUrl.trim().replace(/\/+$/, '');
  if (!url) return null;
  return url.startsWith('http') ? url : `https://${url}`;
}

/**
 * Bepaalt de definitieve, publieke url van een blog: één bron van waarheid,
 * dezelfde url die overal in de app en in nieuwsbrieven/social wordt gebruikt.
 *
 * Volgorde van voorkeur:
 *   1. Gekoppelde WordPress-site (per blog, alleen als de post daar gepubliceerd
 *      en publiek bereikbaar is; `wpPostUrl` staat alleen gevuld bij een
 *      gepubliceerde post, een lokale/test-WordPress wordt eruit gefilterd)
 *   2. joepa-site
 *   3. Eigen domein (widget)
 *   4. lees-nu (met of zonder gebruikersnaam)
 */
export function generateBlogUrl(blogSlug: string, config: BlogUrlConfig, wpPostUrl?: string | null): string {
  if (isPublicHttpUrl(wpPostUrl)) {
    return wpPostUrl as string;
  }

  const joepa = joepaBase(config);
  if (joepa) {
    return `${joepa}/blog/${blogSlug}`;
  }

  if (config.useCustomDomain && config.customDomain) {
    const domain = config.customDomain.replace(/\/$/, '');
    return `${domain}#artikel/${blogSlug}`;
  }

  if (config.usernameSlug) {
    return `https://lees-nu.nl/blog/${config.usernameSlug}/${blogSlug}`;
  }

  return `https://lees-nu.nl/blog/${blogSlug}`;
}

export function generateWidgetBlogUrl(blogSlug: string, config: BlogUrlConfig, wpPostUrl?: string | null): string {
  if (isPublicHttpUrl(wpPostUrl)) {
    return wpPostUrl as string;
  }

  const joepa = joepaBase(config);
  if (joepa) {
    return `${joepa}/blog/${blogSlug}`;
  }

  if (config.useCustomDomain && config.customDomain) {
    const domain = config.customDomain.replace(/\/$/, '');
    return `${domain}#artikel/${blogSlug}`;
  }

  if (config.usernameSlug) {
    return `https://lees-nu.nl/blog/${config.usernameSlug}/${blogSlug}`;
  }

  return `https://lees-nu.nl/blog/${blogSlug}`;
}

export function generateBlogListUrl(config: BlogUrlConfig): string {
  const joepa = joepaBase(config);
  if (joepa) {
    return `${joepa}/blog`;
  }

  if (config.useCustomDomain && config.customDomain) {
    const domain = config.customDomain.replace(/\/$/, '');
    return domain;
  }

  if (config.usernameSlug) {
    return `https://lees-nu.nl/blog/${config.usernameSlug}`;
  }

  return 'https://lees-nu.nl/blog';
}

/**
 * Is dit een publiek bereikbare http(s)-URL? Gebruikt om door de
 * WordPress-plugin teruggemelde post-URLs te filteren vóór ze in
 * social posts of campagnemails belanden: een lokale of interne
 * test-WordPress (localhost, privé-IP, .local/.test) levert anders
 * een dode link op. Bij twijfel: false, dan pakt de aanroeper de
 * publieke dibbi-blog-URL als fallback.
 */
export function isPublicHttpUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return false;
  }

  const host = parsed.hostname.toLowerCase();
  if (
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    host === '0.0.0.0' ||
    host === '::1' ||
    host === '[::1]' ||
    host.startsWith('127.')
  ) {
    return false;
  }
  if (
    host.endsWith('.local') ||
    host.endsWith('.test') ||
    host.endsWith('.internal') ||
    host.endsWith('.example')
  ) {
    return false;
  }
  if (host.startsWith('10.') || host.startsWith('192.168.')) {
    return false;
  }
  const m172 = host.match(/^172\.(\d{1,3})\./);
  if (m172) {
    const second = Number(m172[1]);
    if (second >= 16 && second <= 31) return false;
  }
  return true;
}

export async function getBlogUrlConfigForUser(userId: string, supabase: any): Promise<BlogUrlConfig> {
  const { data: user } = await supabase
    .from('users')
    .select('username_slug, joepa_active, joepa_site_url')
    .eq('id', userId)
    .maybeSingle();

  const { data: settings } = await supabase
    .from('user_blog_settings')
    .select('custom_domain, use_custom_domain')
    .eq('user_id', userId)
    .maybeSingle();

  return {
    useCustomDomain: settings?.use_custom_domain || false,
    customDomain: settings?.custom_domain || null,
    usernameSlug: user?.username_slug || null,
    joepaSiteUrl: user?.joepa_active ? user?.joepa_site_url || null : null,
  };
}
